'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';

// ─── Types ───────────────────────────────────────────────────────────────────

export type PrimaryChallenge =
  | 'Cost Reduction' |'Compliance Risk' |'Technical Debt' |'Vendor End-of-Life' |'Modernization Initiative' |'M&A Integration';

export interface DiscoverFormData {
  totalApps: number;
  historicalApps: number;
  complianceApps: number;
  primaryChallenge: PrimaryChallenge | '';
  businessDrivers: string[];
  followUp: Record<string, string>;
}

const BUSINESS_DRIVERS = [
  'Reduce maintenance costs',
  'Eliminate vendor lock-in',
  'Improve audit readiness',
  'Enable cloud migration',
  'Reduce security risk',
  'Free up IT resources',
  'Support digital transformation',
  'Regulatory compliance',
];

const PRIMARY_CHALLENGES: { value: PrimaryChallenge; description: string }[] = [
  { value: 'Cost Reduction', description: 'Maintenance and licensing costs are unsustainable' },
  { value: 'Compliance Risk', description: 'Regulatory or audit exposure from legacy systems' },
  { value: 'Technical Debt', description: 'Aging systems with no active vendor support' },
  { value: 'Vendor End-of-Life', description: 'Vendor has announced EOL for key platforms' },
  { value: 'Modernization Initiative', description: 'Strategic program to modernize the application estate' },
  { value: 'M&A Integration', description: 'Post-merger application consolidation and deduplication' },
];

const FOLLOW_UP_FIELDS: Record<PrimaryChallenge, { key: string; label: string; placeholder: string; type: 'number' | 'text' }[]> = {
  'Cost Reduction': [
    { key: 'annualCostPerApp', label: 'Estimated annual maintenance cost per legacy app (USD)', placeholder: 'e.g. 45000', type: 'number' },
    { key: 'appsTargetedForRetirement', label: 'Number of apps targeted for retirement', placeholder: 'e.g. 12', type: 'number' },
  ],
  'Compliance Risk': [
    { key: 'auditDeadline', label: 'Upcoming audit / regulatory deadline', placeholder: 'e.g. Q2 2026 — SOX audit', type: 'text' },
    { key: 'complianceCriticalApps', label: 'Number of compliance-critical apps', placeholder: 'e.g. 8', type: 'number' },
  ],
  'Technical Debt': [
    { key: 'avgAppAge', label: 'Average app age (years)', placeholder: 'e.g. 14', type: 'number' },
    { key: 'noVendorSupportApps', label: 'Number of apps with no active vendor support', placeholder: 'e.g. 22', type: 'number' },
  ],
  'Vendor End-of-Life': [
    { key: 'vendorEolDate', label: 'Vendor EOL date', placeholder: 'e.g. December 2025', type: 'text' },
    { key: 'affectedApps', label: 'Number of affected apps', placeholder: 'e.g. 7', type: 'number' },
  ],
  'Modernization Initiative': [
    { key: 'modernizationTimeline', label: 'Target modernization timeline (months)', placeholder: 'e.g. 18', type: 'number' },
    { key: 'budgetAllocated', label: 'Budget allocated (USD)', placeholder: 'e.g. 2500000', type: 'number' },
  ],
  'M&A Integration': [
    { key: 'duplicateApps', label: 'Number of duplicate applications', placeholder: 'e.g. 34', type: 'number' },
    { key: 'integrationDeadline', label: 'Integration deadline', placeholder: 'e.g. Q4 2026', type: 'text' },
  ],
};

// ─── Step Indicator ───────────────────────────────────────────────────────────

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-0 mb-8">
      {Array.from({ length: total }).map((_, i) => (
        <React.Fragment key={`step-ind-${i}`}>
          <div className="flex flex-col items-center">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300"
              style={{
                background: i < current ? '#FF6B6B' : i === current ? 'rgba(255,107,107,0.2)' : 'rgba(255,255,255,0.06)',
                border: i <= current ? '2px solid #FF6B6B' : '2px solid rgba(255,255,255,0.12)',
                color: i <= current ? (i < current ? '#fff' : '#FF6B6B') : '#A8B2C1',
              }}
            >
              {i < current ? (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                i + 1
              )}
            </div>
          </div>
          {i < total - 1 && (
            <div
              className="flex-1 h-0.5 mx-1 transition-all duration-300"
              style={{
                background: i < current ? '#FF6B6B' : 'rgba(255,255,255,0.08)',
                minWidth: '24px',
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Step Labels ─────────────────────────────────────────────────────────────

const STEP_LABELS = [
  'Application Inventory',
  'Primary Challenge',
  'Business Drivers',
  'Follow-up Questions',
];

// ─── Main Wizard ──────────────────────────────────────────────────────────────

export default function DiscoverWizard() {
  const router = useRouter();

  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState<DiscoverFormData>({
    totalApps: 0,
    historicalApps: 0,
    complianceApps: 0,
    primaryChallenge: '',
    businessDrivers: [],
    followUp: {},
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load business profile defaults
  useEffect(() => {
    const raw = localStorage.getItem('smarck_business_profile');
    if (raw) {
      try {
        const profile = JSON.parse(raw);
        if (profile.appPortfolioSize > 0) {
          setFormData((prev) => ({ ...prev, totalApps: profile.appPortfolioSize }));
        }
      } catch {}
    }
  }, []);

  // ── Validation per step ───────────────────────────────────────────────────

  const validateStep0 = () => {
    let errs: Record<string, string> = {};
    if (!formData.totalApps || formData.totalApps <= 0) {
      errs.totalApps = 'Total applications must be greater than 0.';
    }
    if (formData.historicalApps < 0) {
      errs.historicalApps = 'Cannot be negative.';
    }
    if (formData.historicalApps > formData.totalApps) {
      errs.historicalApps = 'Cannot exceed total applications.';
    }
    if (formData.complianceApps < 0) {
      errs.complianceApps = 'Cannot be negative.';
    }
    if (formData.complianceApps > formData.totalApps) {
      errs.complianceApps = 'Cannot exceed total applications.';
    }
    return errs;
  };

  const validateStep1 = () => {
    let errs: Record<string, string> = {};
    if (!formData.primaryChallenge) {
      errs.primaryChallenge = 'Please select a primary challenge.';
    }
    return errs;
  };

  const validateStep2 = () => {
    let errs: Record<string, string> = {};
    if (formData.businessDrivers.length === 0) {
      errs.businessDrivers = 'Please select at least one business driver.';
    }
    return errs;
  };

  const handleNext = () => {
    let errs: Record<string, string> = {};
    if (step === 0) errs = validateStep0();
    if (step === 1) errs = validateStep1();
    if (step === 2) errs = validateStep2();

    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    setErrors({});
    setStep((s) => s - 1);
  };

  const handleComplete = () => {
    // Store results in sessionStorage for the results page
    // Backend integration point: replace sessionStorage with API call
    sessionStorage.setItem('smarck_discover_data', JSON.stringify(formData));
    router.push('/discovery-results');
  };

  const toggleDriver = (driver: string) => {
    setFormData((prev) => ({
      ...prev,
      businessDrivers: prev.businessDrivers.includes(driver)
        ? prev.businessDrivers.filter((d) => d !== driver)
        : [...prev.businessDrivers, driver],
    }));
  };

  const setFollowUp = (key: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      followUp: { ...prev.followUp, [key]: value },
    }));
  };

  // ── Step Renders ──────────────────────────────────────────────────────────

  const renderStep0 = () => (
    <div className="flex flex-col gap-5">
      <div>
        <label className="block text-xs font-semibold mb-1.5 tracking-wide uppercase" style={{ color: '#A8B2C1', letterSpacing: '0.08em' }}>
          Total Applications in Portfolio <span style={{ color: '#FF6B6B' }}>*</span>
        </label>
        <p className="text-xs mb-2" style={{ color: '#A8B2C1' }}>
          Include all applications currently maintained by IT, regardless of usage frequency.
        </p>
        <input
          type="number"
          value={formData.totalApps || ''}
          onChange={(e) => setFormData((p) => ({ ...p, totalApps: parseInt(e.target.value) || 0 }))}
          placeholder="e.g. 240"
          className={`input-base ${errors.totalApps ? 'input-error' : ''}`}
          min="1"
          suppressHydrationWarning
        />
        {errors.totalApps && (
          <p className="text-xs mt-1.5" style={{ color: '#EF4444' }}>{errors.totalApps}</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold mb-1.5 tracking-wide uppercase" style={{ color: '#A8B2C1', letterSpacing: '0.08em' }}>
          Historical-Access-Only Applications <span style={{ color: '#FF6B6B' }}>*</span>
        </label>
        <p className="text-xs mb-2" style={{ color: '#A8B2C1' }}>
          Applications used only for read-only historical data access — no active transactions or updates.
        </p>
        <input
          type="number"
          value={formData.historicalApps || ''}
          onChange={(e) => setFormData((p) => ({ ...p, historicalApps: parseInt(e.target.value) || 0 }))}
          placeholder="e.g. 62"
          className={`input-base ${errors.historicalApps ? 'input-error' : ''}`}
          min="0"
          suppressHydrationWarning
        />
        {errors.historicalApps && (
          <p className="text-xs mt-1.5" style={{ color: '#EF4444' }}>{errors.historicalApps}</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold mb-1.5 tracking-wide uppercase" style={{ color: '#A8B2C1', letterSpacing: '0.08em' }}>
          Applications with Active Compliance / Audit Requirements <span style={{ color: '#FF6B6B' }}>*</span>
        </label>
        <p className="text-xs mb-2" style={{ color: '#A8B2C1' }}>
          Applications subject to regulatory mandates, audit trails, or data retention obligations.
        </p>
        <input
          type="number"
          value={formData.complianceApps || ''}
          onChange={(e) => setFormData((p) => ({ ...p, complianceApps: parseInt(e.target.value) || 0 }))}
          placeholder="e.g. 28"
          className={`input-base ${errors.complianceApps ? 'input-error' : ''}`}
          min="0"
          suppressHydrationWarning
        />
        {errors.complianceApps && (
          <p className="text-xs mt-1.5" style={{ color: '#EF4444' }}>{errors.complianceApps}</p>
        )}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="flex flex-col gap-3">
      {errors.primaryChallenge && (
        <p className="text-xs px-3 py-2 rounded" style={{ color: '#EF4444', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          {errors.primaryChallenge}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {PRIMARY_CHALLENGES.map((challenge) => {
          const isSelected = formData.primaryChallenge === challenge.value;
          return (
            <button
              key={`challenge-${challenge.value}`}
              onClick={() => setFormData((p) => ({ ...p, primaryChallenge: challenge.value }))}
              className="text-left p-4 rounded-lg transition-all duration-150"
              style={{
                background: isSelected ? 'rgba(255,107,107,0.1)' : 'rgba(255,255,255,0.03)',
                border: isSelected ? '1.5px solid rgba(255,107,107,0.5)' : '1px solid rgba(255,255,255,0.08)',
                transform: isSelected ? 'none' : 'none',
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0 mt-0.5 transition-all"
                  style={{
                    border: isSelected ? '5px solid #FF6B6B' : '2px solid rgba(255,255,255,0.2)',
                  }}
                />
                <div>
                  <p className="text-sm font-semibold" style={{ color: isSelected ? '#FF6B6B' : '#FFFFFF' }}>
                    {challenge.value}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: '#A8B2C1' }}>
                    {challenge.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="flex flex-col gap-3">
      {errors.businessDrivers && (
        <p className="text-xs px-3 py-2 rounded" style={{ color: '#EF4444', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          {errors.businessDrivers}
        </p>
      )}
      <p className="text-xs mb-1" style={{ color: '#A8B2C1' }}>
        Select all that apply to your organization's retirement initiative.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {BUSINESS_DRIVERS.map((driver) => {
          const isChecked = formData.businessDrivers.includes(driver);
          return (
            <button
              key={`driver-${driver}`}
              onClick={() => toggleDriver(driver)}
              className="flex items-center gap-3 p-3.5 rounded-lg text-left transition-all duration-150"
              style={{
                background: isChecked ? 'rgba(78,205,196,0.08)' : 'rgba(255,255,255,0.03)',
                border: isChecked ? '1.5px solid rgba(78,205,196,0.4)' : '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <div
                className="w-4 h-4 rounded flex-shrink-0 flex items-center justify-center transition-all"
                style={{
                  background: isChecked ? '#4ECDC4' : 'transparent',
                  border: isChecked ? '2px solid #4ECDC4' : '2px solid rgba(255,255,255,0.2)',
                }}
              >
                {isChecked && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0D1B2A" strokeWidth="3.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
              <span className="text-sm" style={{ color: isChecked ? '#FFFFFF' : '#A8B2C1' }}>
                {driver}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderStep3 = () => {
    if (!formData.primaryChallenge) return null;
    const fields = FOLLOW_UP_FIELDS[formData.primaryChallenge];
    return (
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg" style={{ background: 'rgba(78,205,196,0.08)', border: '1px solid rgba(78,205,196,0.2)' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ECDC4" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-xs" style={{ color: '#4ECDC4' }}>
            Follow-up questions for: <strong>{formData.primaryChallenge}</strong>
          </span>
        </div>
        {fields.map((field) => (
          <div key={`followup-${field.key}`}>
            <label className="block text-xs font-semibold mb-1.5 tracking-wide uppercase" style={{ color: '#A8B2C1', letterSpacing: '0.08em' }}>
              {field.label}
            </label>
            <input
              type={field.type}
              value={formData.followUp[field.key] || ''}
              onChange={(e) => setFollowUp(field.key, e.target.value)}
              placeholder={field.placeholder}
              className="input-base"
              min={field.type === 'number' ? '0' : undefined}
              suppressHydrationWarning
            />
          </div>
        ))}
        <p className="text-xs mt-1" style={{ color: '#A8B2C1' }}>
          These fields are optional but improve the precision of your validation action plan.
        </p>
      </div>
    );
  };

  const steps = [renderStep0, renderStep1, renderStep2, renderStep3];
  const isLastStep = step === 3;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#4ECDC4', letterSpacing: '0.1em' }}>
            GUIDED QUESTIONNAIRE
          </span>
        </div>
        <h1 className="text-2xl font-bold text-white mb-1">Discovery Assessment</h1>
        <p className="text-sm" style={{ color: '#A8B2C1' }}>
          Complete all steps to generate your indicative retirement opportunity assessment.
        </p>
      </div>

      {/* Step indicator */}
      <StepIndicator current={step} total={4} />

      {/* Step label */}
      <div className="mb-6">
        <p className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#A8B2C1', letterSpacing: '0.1em' }}>
          STEP {step + 1} OF 4
        </p>
        <h2 className="text-lg font-bold text-white mt-0.5">{STEP_LABELS[step]}</h2>
      </div>

      {/* Step content card */}
      <div className="rounded-xl p-6 mb-6" style={{ background: '#111F30', border: '1px solid rgba(255,255,255,0.08)' }}>
        {steps[step]?.()}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={step === 0}
          className="btn-ghost"
          style={{ opacity: step === 0 ? 0.3 : 1 }}
          suppressHydrationWarning
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>

        {isLastStep ? (
          <button onClick={handleComplete} className="btn-primary" suppressHydrationWarning>
            Generate Assessment
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        ) : (
          <button onClick={handleNext} className="btn-primary" suppressHydrationWarning>
            Next Step
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}